import { useState, useEffect } from "react";
import "./kiosk.scss";

// Package Imports
import { useIdleTimer } from "react-idle-timer";

// Component Imports
import Modal from "../../components/Modal";
import ScreenSaver from "../../components/ScreenSaver";
import Brand from "../../shared/sj-brand";

// Material UI Imports
import { Grid } from "@mui/material";
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Brand Import
import KioskLogo from "../../assets/logo_FESJC.svg";

// Thumbnail Imports
import beyondStJudeThumbnail from "../../assets/img_beyond-stjude.png";
import buildingStJudeThumbnail from "../../assets/img_building-stjude.png";
import patientCrossThumbnail from "../../assets/img_patient-cross.png";
import stJudeGlobalThumbnail from "../../assets/img_stjude-global.png";

// Video Imports
import beyondStJudeVid from "../../assets/vid_beyond-stjude.mp4";
import buildingStJudeVid from "../../assets/vid_building-stjude.mp4";
import patientCrossVid from "../../assets/vid_patient-cross.mp4";
import stJudeGlobalVid from "../../assets/vid_stjude-global.mp4";

const tempData = {
	firstRow: [
		{
			id: 1,
			index: "one",
			title: "Beyond St. Jude",
			thumbnail: beyondStJudeThumbnail,
			videoUrl: beyondStJudeVid,
			videoDur: "3:34",
		},
		{
			id: 2,
			index: "two",
			title: "Building St. Jude",
			thumbnail: buildingStJudeThumbnail,
			videoUrl: buildingStJudeVid,
			videoDur: "4:04",
		},
	],
	secondRow: [
		{
			id: 3,
			index: "three",
			title: "Patient Cross",
			thumbnail: patientCrossThumbnail,
			videoUrl: patientCrossVid,
			videoDur: "2:56",
		},
		{
			id: 4,
			index: "four",
			title: "St. Jude - Global",
			thumbnail: stJudeGlobalThumbnail,
			videoUrl: stJudeGlobalVid,
			videoDur: "1:00",
		},
	],
};

const Kiosk = () => {
	// Variables - START ----------------------------------------------------
	const IDLE_SECONDS = 15;
	// Variables - END ------------------------------------------------------

	// Hooks/State - START --------------------------------------------------
	const [modals, setModals] = useState({
		one: false,
		two: false,
		three: false,
		four: false,
		five: false,
		six: false,
	});
	const [screensaverOpen, setScreensaverOpen] = useState(false);
	const [analytics, setAnalytics] = useState([]);
	// Hooks/State - END ----------------------------------------------------

	// Methods - START ------------------------------------------------------
	const onActive = (event) => {
		idleTimer.reset();
		setModals({
			one: false,
			two: false,
			three: false,
			four: false,
		});
		setScreensaverOpen(false);
	};

	const onIdle = (event) => {
		setScreensaverOpen(true);
	};

	const idleTimer = useIdleTimer({
		timeout: 1000 * IDLE_SECONDS,
		startOnMount: true,
		startManually: false,
		stopOnIdle: false,
		onActive,
		onIdle,
		events: [
			"mousemove",
			"keydown",
			"wheel",
			"DOMMouseScroll",
			"mousewheel",
			"mousedown",
			"touchstart",
			"touchmove",
			"MSPointerDown",
			"MSPointerMove",
			"visibilitychange",
		],
	});

	const handleModalOpen = (modalIdx) => {
		setModals({ ...modals, [modalIdx]: true });
	};

	const handleModalClose = (modalIdx) => {
		setModals({ ...modals, [modalIdx]: false });
	};

	const pushAnalyticsRow = (modalIdx, action, type, timestamp) => {
		setAnalytics((prevState) => [
			...prevState,
			{ modalIdx, action, type, timestamp },
		]);

		const currentLSArr = localStorage.getItem("fesjc-analytics");

		if (currentLSArr) {
			const parsedArr = JSON.parse(currentLSArr);

			const newArr = [...parsedArr, { modalIdx, action, type, timestamp }];

			const stringifiedArr = JSON.stringify(newArr);
			localStorage.setItem("fesjc-analytics", stringifiedArr);
		} else {
			const tempObj = [
				{
					modalIdx,
					action,
					type,
					timestamp,
				},
			];

			const stringifiedArr = JSON.stringify(tempObj);

			localStorage.setItem("fesjc-analytics", stringifiedArr);
		}
	};
	// Methods - END --------------------------------------------------------

	// useEffects - START ---------------------------------------------------
	useEffect(() => {
		if (!modals.one && !modals.two && !modals.three && !modals.four) {
			idleTimer.reset();
			idleTimer.resume();
		}

		if (modals.one || modals.two || modals.three || modals.four) {
			idleTimer.pause();
		}
	}, [modals]);
	// useEffects - END -----------------------------------------------------

	return (
		<Grid
			container
			direction='row'
			justifyContent='center'
			alignItems='center'
			className='app-container'
		>
			{screensaverOpen ? (
				<ScreenSaver />
			) : (
				<>
					{/* LEFT COLUMN */}
					<Grid item container md={3} className="kiosk-branding">
						<img src={KioskLogo} className='kiosk-logo' alt='Brand Logo' />
						<div className='sj-brand'>
							<Brand />
						</div>
					</Grid>

					{/* GALLERY / RIGHT COLUMN */}
					<Grid
						item
						container
						md={9}
						className='video-container'
						direction='column'
						justifyContent='center'
					>
						{/* Gallery First Row */}
						<Grid
							item
							container
							className='video-container-first-row'
							direction='row'
							justifyContent='space-between'
							alignItems='center'
						>
							{tempData.firstRow.map((row) => {
								return (
									<Grid
										item
										className='video-element'
										key={row.index}
										onClick={() => {
											const timestamp = new Date();
											handleModalOpen(row.index);
											pushAnalyticsRow(row.index, "open", "user", timestamp);
										}}
										style={{
											background: `#fff url(${row.thumbnail}) no-repeat`,
											backgroundSize: "cover",
											backgroundPosition: "top 0px center",
											backgroundBlendMode: "multiply",
										}}
									>
										<div className='video-thumb-title'>
											<h3>{row.title}</h3>
											<div className='video-duration'>
												<AccessTimeIcon className="dur-icon"/>
												{row.videoDur}
											</div>
										</div>
									</Grid>
								);
							})}
						</Grid>

						{/* Gallery Second Row */}
						<Grid
							item
							container
							className='video-container-second-row'
							direction='row'
							justifyContent='space-between'
							alignItems='center'
						>
							{tempData.secondRow.map((row) => {
								return (
									<Grid
										item
										className='video-element'
										key={row.index}
										onClick={() => {
											const timestamp = new Date();
											handleModalOpen(row.index);
											pushAnalyticsRow(row.index, "open", "user", timestamp);
										}}
										style={{
											background: `#fff url(${row.thumbnail}) no-repeat`,
											backgroundSize: "cover",
											backgroundPosition: "top 0px center",
											backgroundBlendMode: "multiply",
										}}
									>
										<div className='video-thumb-title'>
											<h3>{row.title}</h3>
											<div className='video-duration'>
												<AccessTimeIcon className="dur-icon"/>
												{row.videoDur}
											</div>
										</div>
									</Grid>
								);
							})}
						</Grid>
					</Grid>

					{/* Modals */}
					{tempData.firstRow.map((row) => {
						return (
							<Modal
								key={row.index}
								handleModalClose={handleModalClose}
								open={modals[row.index]}
								data={row}
								pushAnalyticsRow={pushAnalyticsRow}
							/>
						);
					})}

					{tempData.secondRow.map((row) => {
						return (
							<Modal
								key={row.index}
								handleModalClose={handleModalClose}
								open={modals[row.index]}
								data={row}
								pushAnalyticsRow={pushAnalyticsRow}
							/>
						);
					})}
				</>
			)}
		</Grid>
	);
};

export default Kiosk;
